import { PrimaryButton, SecondaryButton } from "common/Forms";
import { ActionBar, CommonPage, Content, InRow, LoadableContent, OpenablePanel, PageTitle, ResponsiveGap } from "common/Grid"
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GrClose } from "react-icons/gr";
import { useSelector } from "react-redux";
import { useAdminFeeMutation } from "store/action";
import { AdminFeeAdvanceSearchForm } from "./Admin-fee-filter";
import { createSearchParams, useSearchParams } from "react-router-dom";
import { AdminFeeStatusCard } from "./fee-status-card";
import { convertDateToJson, convertJsonToDate } from "Constant/utils";
import { useGetSiteIdOptionsMutation } from "store/action/options-store";

export const Page = () => {
  const [getAdminFeeApi, {isLoading, isError}] = useAdminFeeMutation();
  const [showSearch, setShowSearch] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const pagedAdminOrderInfo = useSelector((state) => state?.adminFees?.data?.data?.summaryReport);
  const isForceApplySearch = useRef(false);
  const isDeferredApply = useRef(false);
  const [getSiteIdOptionsApi] = useGetSiteIdOptionsMutation();
  const [siteIdOptions, setSiteIdOptions] = useState([]);

  const searchFields = useMemo(() =>
  ({
    startDateFrom: { name: 'startDateFrom', default: undefined, },
    endDateFrom: { name: 'endDateFrom', default: undefined, },
    siteid: { name: 'siteid', default: undefined, },
    orderingOptions: { name: 'orderingOptions', default: undefined, },
    payment: { name: 'payment', default: undefined, },
  }), []);

  const getSearchValue = useCallback((name, defaultValue, getter = (value) => value) => {
    const value = searchParams.get(name);
    if (value === null) {
      return defaultValue;
    }
    return getter(value);
  }, [searchParams])

  const [advancedSearchFormUserInput, setAdvancedSearchFormUserInput] = useState({
    startDateFrom: getSearchValue(searchFields.startDateFrom.name, searchFields.startDateFrom.default, value => convertJsonToDate(value)),
    endDateFrom: getSearchValue(searchFields.endDateFrom.name, searchFields.endDateFrom.default, value => convertJsonToDate(value)),
    siteid: getSearchValue(searchFields.siteid.name, searchFields.siteid.default),
    orderingOptions: getSearchValue(searchFields.orderingOptions.name, searchFields.orderingOptions.default),
    payment: getSearchValue(searchFields.payment.name, searchFields.payment.default),
  });

  const [isAdvancedSearch, setIAdvancedSearch] = useState();

  const searchAsync = useCallback(async () => {
    await getAdminFeeApi(
      {
        startDate: getSearchValue(searchFields.startDateFrom.name, searchFields?.startDateFrom.default),
        endDate: getSearchValue(searchFields.endDateFrom.name, searchFields?.endDateFrom.default),
        siteId: getSearchValue(searchFields.siteid.name, searchFields.siteid.default),
        paymentMethod: getSearchValue(searchFields.payment.name, searchFields.payment.default),
        option: getSearchValue(searchFields.orderingOptions.name, searchFields.orderingOptions.default),
      }
    );
  }, [getAdminFeeApi, getSearchValue, searchFields]);

  const applySearch = useCallback(() => {
    const updatedSearchParams = createSearchParams(searchParams);
    advancedSearchFormUserInput.startDateFrom ? updatedSearchParams.set(searchFields.startDateFrom.name, convertDateToJson(advancedSearchFormUserInput.startDateFrom, true) ?? '') : updatedSearchParams.delete(searchFields.startDateFrom.name);
    advancedSearchFormUserInput.endDateFrom ? updatedSearchParams.set(searchFields.endDateFrom.name, convertDateToJson(advancedSearchFormUserInput.endDateFrom, true) ?? '') : updatedSearchParams.delete(searchFields.endDateFrom.name);
    advancedSearchFormUserInput.siteid ? updatedSearchParams.set(searchFields.siteid.name, advancedSearchFormUserInput.siteid) : updatedSearchParams.delete(searchFields.siteid.name);
    advancedSearchFormUserInput.payment ? updatedSearchParams.set(searchFields.payment.name, advancedSearchFormUserInput.payment) : updatedSearchParams.delete(searchFields.payment.name);
    advancedSearchFormUserInput.orderingOptions ? updatedSearchParams.set(searchFields.orderingOptions.name, advancedSearchFormUserInput.orderingOptions) : updatedSearchParams.delete(searchFields.orderingOptions.name);
    setSearchParams(updatedSearchParams);
  }, [searchFields, searchParams, advancedSearchFormUserInput, setSearchParams]);

  const onCancelSearchHandler = () => {
    setIAdvancedSearch(false);
  };

  useEffect(() => {
    searchAsync();
  }, [searchAsync]);

  useEffect(() => {
    if (isDeferredApply.current) {
      isDeferredApply.current = false;
      return;
    }
    if (isAdvancedSearch || !isForceApplySearch.current) {
      isForceApplySearch.current = false;
      applySearch();
    }
  }, [advancedSearchFormUserInput]);

  useEffect(() => {
    getSiteIdOptionsApi().then(response => {
      const options = response?.data?.data ?? [];
      setSiteIdOptions(options);
    });
  }, [getSiteIdOptionsApi]);
  return (
    <CommonPage>
      <Content>
        <PageTitle title="Admin Fee" />
        <ResponsiveGap left={false} right={false}>
          <OpenablePanel isOpenedByDefault={showSearch}
            openControlRenderer=
            {
              action => <ActionBar classNames={['justify-end']}><PrimaryButton action={action}>Advanced Search</PrimaryButton></ActionBar>
            }
            closeControlRenderer=
            {
              action =>
                <ResponsiveGap top={false} left={false} right={false}>
                  <ActionBar classNames={['justify-end']}>
                    <InRow><SecondaryButton action={action}><GrClose size={20} /></SecondaryButton></InRow>
                  </ActionBar>
                </ResponsiveGap>
            }
            onClose={onCancelSearchHandler}
            onOpen={() => setShowSearch(true)}>
            <AdminFeeAdvanceSearchForm
              siteIdOptions={siteIdOptions}
              userInput={advancedSearchFormUserInput}
              saveUserInput={(input, deferApply = false) => {
                isDeferredApply.current = deferApply;
                setAdvancedSearchFormUserInput(input);
              }}
            />
          </OpenablePanel>
        </ResponsiveGap>
        <LoadableContent isLoading={isLoading}
                         isError={isError}>
          <AdminFeeStatusCard feeCards={pagedAdminOrderInfo}/>
        </LoadableContent>
      </Content>
    </CommonPage>
  )
}
